import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 font-heading text-xl font-bold whitespace-nowrap cursor-pointer"
          >
            <span className={`w-9 h-9 flex items-center justify-center rounded-lg text-base ${scrolled || !isHome ? 'bg-primary-500 text-background-50' : 'bg-background-50/20 text-background-50'}`}>
              <i className="ri-download-2-fill"></i>
            </span>
            <span className={scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'}>
              磁力猫
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? scrolled
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-background-50/20 text-background-50'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? scrolled || !isHome
                      ? 'bg-primary-50 text-primary-700'
                      : 'bg-background-50/20 text-background-50'
                    : scrolled || !isHome
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
                }`}
              >
                资源分类
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCategoryOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-xl border border-background-200/80 py-2 z-20">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 text-sm">
                          <i className={cat.icon}></i>
                        </span>
                        <span>{cat.name}</span>
                        <span className="ml-auto text-xs text-foreground-400">{cat.count.toLocaleString()}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? scrolled || !isHome
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-background-50/20 text-background-50'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-background-50/10'
              }`}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              scrolled || !isHome ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/10'
            }`}
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              资源分类
            </div>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm cursor-pointer ${
                  location.pathname === `/category/${cat.slug}`
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 text-sm">
                  <i className={cat.icon}></i>
                </span>
                <span>{cat.name}</span>
              </Link>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/about'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}